import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ListingFomoBadges as Badges } from "@/types";

const MIN_SAVINGS_PERCENT = 10;
const HIGH_DEMAND_THRESHOLD = 8;

type Highlight = {
  label: ReactNode;
  className: string;
  icon: ReactNode;
};

function pickHighlight(badges: Badges, t: (k: string, opts?: Record<string, unknown>) => string): Highlight | null {
  if (badges.priceDropped && badges.previousPrice != null) {
    return {
      label: t("fomo.price_dropped"),
      className: "bg-rose-600 text-white",
      icon: (
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.6}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      ),
    };
  }

  if (badges.wishlistCount >= HIGH_DEMAND_THRESHOLD) {
    return {
      label: t("fomo.watching_short", { count: badges.wishlistCount }),
      className: "bg-pink-600 text-white",
      icon: (
        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
      ),
    };
  }

  if (badges.uniqueInMarketplace) {
    return {
      label: t("fomo.unique_short"),
      className: "bg-purple-600 text-white",
      icon: (
        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2l2.39 4.84L19.8 7.6l-3.9 3.81.92 5.37L12 14.27l-4.82 2.52.92-5.37L4.2 7.6l5.41-.76L12 2z" />
        </svg>
      ),
    };
  }

  return null;
}

export function ListingFomoHighlight({ badges }: { badges: Badges | null | undefined }) {
  const { t } = useTranslation();
  if (!badges) return null;
  const highlight = pickHighlight(badges, t);
  if (!highlight) return null;

  return (
    <div
      className={`pointer-events-none absolute left-2 top-2 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide shadow-sm ${highlight.className}`}
    >
      {highlight.icon}
      <span>{highlight.label}</span>
    </div>
  );
}

export type ComparisonPriceKind = "previous" | "shop";

export interface ComparisonPriceInfo {
  amount: number;
  kind: ComparisonPriceKind;
  savingsPercent: number | null;
}

export function listingComparisonInfo(
  badges: Badges | null | undefined,
): ComparisonPriceInfo | null {
  if (!badges) return null;
  if (badges.priceDropped && badges.previousPrice != null) {
    return {
      amount: badges.previousPrice,
      kind: "previous",
      savingsPercent: null,
    };
  }
  if (
    badges.savingsVsShops != null &&
    badges.savingsVsShops > 0 &&
    badges.cheapestShopPrice != null &&
    badges.savingsPercent != null &&
    badges.savingsPercent >= MIN_SAVINGS_PERCENT
  ) {
    return {
      amount: badges.cheapestShopPrice,
      kind: "shop",
      savingsPercent: badges.savingsPercent,
    };
  }
  return null;
}

export function listingComparisonPrice(badges: Badges | null | undefined): number | null {
  return listingComparisonInfo(badges)?.amount ?? null;
}

export function isFreshListing(badges: Badges | null | undefined): boolean {
  if (!badges) return false;
  if (!badges.recent) return false;
  // Suppress "new" indicator when a stronger signal already takes the highlight slot.
  if (badges.priceDropped) return false;
  if (badges.savingsPercent != null && badges.savingsPercent >= MIN_SAVINGS_PERCENT) return false;
  if (badges.wishlistCount >= HIGH_DEMAND_THRESHOLD) return false;
  if (badges.uniqueInMarketplace) return false;
  return true;
}
